(ns manage.adset-decision-tree-page.view.common
  (:require ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Tooltip" :default Tooltip :refer [tooltipClasses]]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/system" :refer [styled]]
            ["react-hook-form" :as rhf]
            ["src/svg/arrows/arrow-clockwise-2.svg$default" :as ArrowClockwise2]
            ["src/svg/controls/watch.svg$default" :as Watch]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            ["src/svg/wpp/content/calendar.svg$default" :as Calendar]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.queries :refer [use-query]]
            [common.resource.query.decision-tree :as queries]
            [common.util :refer [spread-obj spread-props format-iso-string]]
            [common.widgets.form-field :refer [form-field]]
            [common.widgets.menu :refer [menu]]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.hook :as hook]
            [manage.adset-integration-meta-settings.db :as meta-settings-db]
            [re-frame.core :as rf]
            [uix.core :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def no-max-width-tooltip
  ((styled
    (fn [props]
      ($ Tooltip
         (spread-props (->js (dissoc (->clj props) :className))
                       #js {:classes #js {:popper (.-className props)}}))))
   (fn [^js _props]
     (->js {(str "& ." (.-tooltip tooltipClasses))
            #js {:maxWidth "none"}}))))

(defui decision-tree-node-container
  [{:keys [selected dashed children paper-props sx]}]
  ($ Paper (merge {:sx (spread-obj
                        #js {:p 2
                             :outline (when (or selected dashed) 2)
                             :outlineColor "primary.main"
                             :outlineStyle (cond
                                             dashed "dashed"
                                             selected "solid")
                             :borderRadius 2
                             :width "320px"}
                        sx)}
                  paper-props)
     children))

(defui node-icon [{:keys [icon raw-icon]}]
  ($ SvgIcon {:component icon
              :sx (cond-> {:width 24
                           :height 24}
                    (not raw-icon) (assoc :color "background.paper"
                                          :backgroundColor "primary.main"
                                          :borderRadius 1
                                          :p "1px")

                    true clj->js)}))

(defui node-header [{{node-name :name} :node-data
                     :keys [sx children node-data]}]
  ($ Stack {:direction "row"
            :alignItems "center"
            :sx sx}
     ($ node-icon node-data)
     ($ Typography {:ml 1
                    :mr "auto"
                    :variant "h3"}
        node-name)
     children))

(defui outcome-label [{:keys [label]}]
  ($ Typography {:align "center"
                 :sx #js {:overflowWrap "anywhere"
                          :ml 2
                          :mr "auto"}}
     label))

(defui chip [{:keys [bold sx uppercase label] :as props
              :or {bold true
                   uppercase true}}]
  (when-not (str/blank? label)
    ($ Chip (merge {:variant "subtle"
                    :sx (spread-obj #js {:fontWeight (if bold 700 500)
                                         :textTransform (when uppercase "uppercase")}
                                    sx)}
                   (dissoc props :bold :sx :uppercase)))))

(defui warning-icon [{:keys [tooltip-title]}]
  ($ Tooltip {:placement "bottom"
              :title tooltip-title}
     ($ SvgIcon {:color "warning"
                 :viewBox "0 0 22 22"
                 :component AlertIcon})))

(defui content-id-text [{:keys [content-id child-count sx]}]
  ($ Typography {:sx (spread-obj #js {:overflow "hidden"
                                      :textOverflow "ellipsis"
                                      :whiteSpace "nowrap"}
                                 sx)}
     content-id
     (when child-count
       (str " (" child-count ")"))))

(defui product-store-menu [{:keys [on-product-selector]}]
  ($ IconButton
     {:onClick on-product-selector}
     ($ Icon
        {:color "default"
         :sx #js {:fontSize "inherit"}}
        "shopping_cart")))

(defui ad-variant-menu [{:keys [on-remove read-only]}]
  ($ menu {:icon "more_horiz"
           :toggle-props {:sx #js {:visibility (when read-only "hidden")}}
           :items [{:label "Exclude variant"
                    :on-click on-remove}]}))

(defui side-panel-list-item [{:keys [children sx on-click]}]
  ($ ListItem {:sx (spread-obj #js {:borderRadius 1
                                    :borderStyle "solid"
                                    :borderColor "grey.70"
                                    :borderWidth "1px"
                                    :mb 1
                                    :px 1
                                    :py 0.5
                                    :cursor (when on-click "pointer")}
                               sx)
               :on-click on-click}
     children))

(defmodal save-error-modal :decision-tree/save-error-modal
  [{:keys [re-import]}]
  (let [decision-tree-id (use-subscribe [::sub/decision-tree-id])
        url-context (use-subscribe [:url-context])]
    ($ modal-v2
       {:title "Connection Issue Detected"
        :icon ($ SvgIcon {:color "warning"}
                 ($ AlertIcon))
        :footer ($ Stack {:direction "row" :gap 1}
                   ($ Button {:variant "text"
                              :sx #js {:color "text.primary"}
                              :onClick #(rf/dispatch [:modal/close])}
                      "Cancel")
                   ($ Button {:variant "contained"
                              :color "warning"
                              :onClick
                              #(do
                                 (rf/dispatch [:modal/close])
                                 (hook/clean-editor (assoc url-context
                                                           :decision-tree-id decision-tree-id)))}
                      "Refresh"))}
       ($ Stack {:sx #js {:width "450px"}}
          ($ :p "We couldn't save your changes due to a connection issue with our server. The decision tree will revert to the last saved version. Please refresh the tree to restore it and ensure your data is up to date.")))))

(defmodal logged-out-error-modal :decision-tree/logged-out-error-modal
  [{:keys [re-import]}]
  ($ modal-v2
     {:title "Session expired"
      :icon ($ SvgIcon {:color "warning"}
               ($ AlertIcon))
      :footer ($ Button {:variant "contained"
                         :color "warning"
                         :onClick #(.reload js/window.location)}
                 "Login")}
     ($ Stack {:sx #js {:width "450px"}}
        ($ :p "In order to continue editing the Decision Tree please log in again."))))

(defui upgrade-ad-variants-button [{:keys [on-upgrade tooltip read-only]}]
  (if read-only
    ($ Icon {:sx #js {:color "primary.main"
                      :fontSize "small"}}
       "circle")
    ($ Tooltip {:placement "top"
                :title (when tooltip "Update to the latest version")}
       ($ IconButton {:on-click (fn [e]
                                  (.stopPropagation e)
                                  (on-upgrade))
                      :sx #js {:color "primary.main"}}
          ($ SvgIcon {:component ArrowClockwise2})))))

(defui preview-button [{:keys [ids]}]
  ($ IconButton
     {:onClick (fn [e]
                 (.stopPropagation e)
                 (rf/dispatch
                  [:manage.ad-variant-preview.db/open-preview (set ids) false true]))
      :sx #js {:color "grey.10"}}
     ($ Watch)))

(defui autocomplete-id-field
  [{{:keys [error]} :fieldState
    {:keys [onChange value]} :field
    :keys [ids-with-description required no-options-text loading inherited-input-props
           placeholder disabled]}]
  (let [options (map (fn [{:keys [id description disabled tag]}]
                       {:value id
                        :label (if description (str description " - " id) id)
                        :disabled disabled
                        :tag tag})
                     ids-with-description)
        value->option (->> options (map (juxt :value identity)) (into {}))
        input-props (cond-> (or inherited-input-props {})
                      placeholder (assoc :placeholder placeholder)
                      true clj->js)]
    ($ Autocomplete
       {:disableClearable (boolean required)
        :disabled disabled
        :options (clj->js options)
        :getOptionLabel (fn [^js opt]
                          (cond
                            (string? opt) opt
                            opt (.-label opt)
                            :else ""))
        :getOptionDisabled (fn [^js opt] (boolean (.-disabled opt)))
        :isOptionEqualToValue (fn [^js opt ^js val]
                                (= (.-value opt)
                                   (if (string? val) val (.-value val))))
        :noOptionsText (or no-options-text js/undefined)
        :value (or (clj->js (get value->option value)) value "")
        :loading loading
        :onChange (fn [^js _event ^js new-opt]
                    (onChange (when new-opt (.-value new-opt))))
        :renderOption (fn [^js props ^js opt]
                        ($ :li (spread-obj props #js {:style #js {:minHeight "fit-content"}})
                           (.-label opt)
                           (when (.-tag opt)
                             ($ Chip {:label (.-tag opt)
                                      :size "small"
                                      :sx #js {:ml "auto" :verticalAlign "middle"}}))))
        :renderInput (fn [^js props]
                       ($ TextField
                          (spread-props
                           props
                           {:slotProps #js {:input (spread-obj (.-InputProps props)
                                                               input-props)}
                            :error (boolean error)
                            :helperText (:message error)
                            :fullWidth true})))
        :sx #js {".MuiOutlinedInput-root" #js {:paddingRight "0.5rem !important"}}})))

(defui id-input [{:keys [label required] :as props}]
  ($ form-field {:label label
                 :required? required
                 :sx #js {:mb 2}}
     ($ rhf/Controller (assoc (select-keys props [:name :control :rules])
                              :render (fn [^js rhf-props]
                                        ($ autocomplete-id-field
                                           (merge (->clj rhf-props) props)))))))

(defui async-id-input [{:keys [query] :as props}]
  (let [{ids-with-description :data loading :isLoading} (use-query query)]
    ($ id-input (assoc props :ids-with-description ids-with-description :loading loading))))

(defui meta-adset-autocomplete-editor
  [props]
  (let [url-context (use-subscribe [:url-context])
        integration-settings (meta-settings-db/use-integration-settings)
        {:keys [facebook-ad-account-id]} integration-settings
        {:keys [isFetching data]} (use-query (spread-obj
                                              (queries/fetch-meta-adsets-query url-context)
                                              #js {:enabled (boolean (not-empty facebook-ad-account-id))}))
        no-options-text (when (str/blank? facebook-ad-account-id)
                          "Platform settings are missing")]
    ($ id-input (merge {:ids-with-description data
                        :loading isFetching
                        :no-options-text no-options-text}
                       props))))

(defui tiktok-ad-group-autocomplete-editor [props]
  (let [url-context (use-subscribe [:url-context])
        {:keys [isFetching data]} (use-query (queries/fetch-tiktok-ad-groups-query url-context))]
    ($ id-input (merge {:ids-with-description data :loading isFetching} props))))

(defui landing-url-info-tooltip []
  ($ no-max-width-tooltip
     {:title ($ Typography {:sx #js {:maxWidth "50rem"}}
                "Click trackers can use "
                ($ :span {:style {:font-weight 600}} "(:landing_url:)")
                " or "
                ($ :span {:style {:font-weight 600}} "(:landing_url_enc:)")
                " , these will be replaced with the actual landing URL when the link is generated.")
      :placement "top"}
     ($ Icon "info_outlined")))
